import React, { useState, useEffect, useCallback } from 'react';
import { coachingAnalytics } from '../../api';
import TeamSummary from './TeamSummary';
import './TeamHours.css';

const COLORS = ['#2f4ba2', '#e67e22', '#27ae60', '#8e44ad', '#16a085', '#c0392b', '#d4a017', '#7f8c8d'];
const UNITS = [{ key: 'day', label: 'Day' }, { key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }];
const WEEKDAY_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const REFRESH_MS = 5 * 60 * 1000;

// "YYYY-MM-DD" -> a Date whose UTC parts are that calendar day (so the viewer's own timezone never shifts it)
const dateOf = (key) => { const [y, m, d] = key.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const fmtDay = (key, opts = { weekday: 'short', day: 'numeric', month: 'short' }) => dateOf(key).toLocaleDateString([], { ...opts, timeZone: 'UTC' });
const hoursText = (h) => `${Math.round(h * 10) / 10}`;
const hourLabel = (h) => (h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`);

const bucketLabel = (unit, start) => {
  const d = dateOf(start);
  if (unit === 'month') return [d.toLocaleDateString([], { month: 'short', timeZone: 'UTC' }), String(d.getUTCFullYear()).slice(2)];
  return [String(d.getUTCDate()), d.toLocaleDateString([], { month: 'short', timeZone: 'UTC' })];
};
const bucketTitle = (unit, start) => {
  if (unit === 'month') return dateOf(start).toLocaleDateString([], { month: 'long', year: 'numeric', timeZone: 'UTC' });
  if (unit === 'week') return `Week of ${fmtDay(start)}`;
  return fmtDay(start, { weekday: 'long', day: 'numeric', month: 'long' });
};

const Bars = ({ rows, valueOf, labelOf, subOf, unitLabel = 'h' }) => {
  const max = Math.max(...rows.map(valueOf), 0.0001);
  return rows.map((r, i) => (
    <div key={i} className="th-row">
      <span className="th-row-name" title={labelOf(r)}>{labelOf(r)}</span>
      <span className="th-bar"><i style={{ width: `${Math.max(2, (valueOf(r) / max) * 100)}%` }} /></span>
      <span className="th-row-val">{hoursText(valueOf(r))}{unitLabel}{subOf ? ` · ${subOf(r)}` : ''}</span>
    </div>
  ));
};

// Headcoach analytics: coaching hours for every coach they manage, by day / week / month.
const TeamHoursTab = () => {
  const [unit, setUnit] = useState('week');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [openCoach, setOpenCoach] = useState('');
  const [summaryTick, setSummaryTick] = useState(0); // bumped by Refresh so the at-a-glance numbers re-read the calendars too

  const load = useCallback((forceFresh = false) => {
    setLoading(true);
    setError('');
    return coachingAnalytics.get(unit, forceFresh)
      .then(res => { setData(res.data); setSelected(null); })
      .catch(() => setError('Could not load the team hours. Pull down to try again.'))
      .finally(() => setLoading(false));
  }, [unit]);

  useEffect(() => { setData(null); load(); }, [load]);

  useEffect(() => {
    const timer = setInterval(() => { if (!document.hidden) coachingAnalytics.get(unit).then(res => setData(res.data)).catch(() => {}); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [unit]);

  const toggle = (
    <div className="th-top">
      <div className="th-seg" role="group" aria-label="Group hours by">
        {UNITS.map(u => (
          <button key={u.key} type="button" aria-pressed={unit === u.key} onClick={() => setUnit(u.key)}>{u.label}</button>
        ))}
      </div>
      <button type="button" className="th-refresh" onClick={() => { setSummaryTick(t => t + 1); load(true); }} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
    </div>
  );

  if (error && !data) return <div className="th"><div className="th-card">{toggle}<p className="th-error">{error}</p></div></div>;
  if (!data) return <div className="th"><div className="th-card">{toggle}<div className="th-empty">Loading the team's calendars...</div></div></div>;

  const { buckets, coaches, totals, venues, customers, distribution, zeroDays, excluded } = data;
  const colorOf = (i) => COLORS[i % COLORS.length];
  const maxTotal = Math.max(...totals.hoursByBucket, 0.5);
  const chartHeight = 150; // px for the tallest bar
  const sel = selected !== null ? selected : null;
  const noCalendar = coaches.filter(c => c.source === 'none' || c.source === 'error');
  const iphoneCoaches = coaches.filter(c => c.source === 'iphone');
  const periodWord = unit === 'day' ? 'days' : unit === 'week' ? 'weeks' : 'months';
  const maxWeekday = Math.max(...distribution.byWeekday.map(d => d.hours), 0.0001);
  const hours = distribution.byStartHour;
  const maxHour = Math.max(...hours.map(h => h.hours), 0.0001);

  return (
    <div className="th">
      <TeamSummary tick={summaryTick} />

      <div className="th-card">
        {toggle}
        <p className="th-sub" style={{ marginTop: 10 }}>
          Last {buckets.length} {periodWord}, Singapore time. Every timed calendar event counts as a lesson, except buffers, declined events and blocks longer than 6 hours.
        </p>

        <div className="th-tiles">
          <div className="th-tile"><b>{hoursText(totals.hours)}</b><span>Coaching hours</span></div>
          <div className="th-tile"><b>{totals.lessons}</b><span>Lessons</span></div>
          <div className="th-tile"><b>{hoursText(totals.avgHoursPerActiveDay)}</b><span>Hours per active day</span></div>
          <div className="th-tile"><b>{zeroDays.teamCount}</b><span>Days with no lessons</span></div>
        </div>
      </div>

      <div className="th-card">
        <h3 className="th-h">Hours per {unit}</h3>
        <div className="th-chart" role="list">
          <div className="th-grid" style={{ bottom: chartHeight + 2 }}><em>{hoursText(maxTotal)}h</em></div>
          {buckets.map((b, i) => {
            const total = totals.hoursByBucket[i];
            const stackHeight = total > 0 ? Math.max(4, (total / maxTotal) * chartHeight) : 0;
            return (
              <button
                key={b.key}
                type="button"
                role="listitem"
                className={`th-col${total === 0 ? ' zero' : ''}`}
                aria-pressed={sel === i}
                aria-label={`${bucketTitle(unit, b.start)}: ${hoursText(total)} hours`}
                onClick={() => setSelected(sel === i ? null : i)}
              >
                <span className="th-val">{hoursText(total)}</span>
                <span className="th-stack" style={{ height: total === 0 ? undefined : stackHeight }}>
                  {coaches.map((c, ci) => (c.hoursByBucket[i] > 0 ? (
                    <i key={c._id} style={{ height: `${(c.hoursByBucket[i] / total) * 100}%`, background: colorOf(ci) }} />
                  ) : null))}
                </span>
              </button>
            );
          })}
        </div>
        <div className="th-labels">
          {buckets.map(b => { const [top, bottom] = bucketLabel(unit, b.start); return <span key={b.key}><b>{top}</b>{bottom}</span>; })}
        </div>
        <div className="th-legend">
          {coaches.map((c, ci) => <span key={c._id}><i style={{ background: colorOf(ci) }} />{c.name}</span>)}
        </div>
        {sel !== null && (
          <div className="th-detail">
            <strong>{bucketTitle(unit, buckets[sel].start)}</strong>: {hoursText(totals.hoursByBucket[sel])}h, {totals.lessonsByBucket[sel]} {totals.lessonsByBucket[sel] === 1 ? 'lesson' : 'lessons'}
            {coaches.filter(c => c.hoursByBucket[sel] > 0).map(c => <div key={c._id}>{c.name}: {hoursText(c.hoursByBucket[sel])}h ({c.lessonsByBucket[sel]})</div>)}
          </div>
        )}
      </div>

      <div className="th-card">
        <h3 className="th-h">Hours by coach</h3>
        <div className="th-scroll">
          <table className="th-table">
            <thead>
              <tr><th>Coach</th><th>Hours</th><th>Lessons</th><th>Avg</th><th>Busiest day</th><th>No-lesson days</th></tr>
            </thead>
            <tbody>
              {coaches.map((c, ci) => (
                <tr key={c._id}>
                  <td>
                    <i style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: colorOf(ci), marginRight: 6 }} />
                    {c.name}{c.isYou && <span className="th-tag">you</span>}
                    {c.source === 'iphone' && <span className="th-tag warn">iPhone</span>}
                    {(c.source === 'none' || c.source === 'error') && <span className="th-tag warn">no calendar</span>}
                  </td>
                  <td>{hoursText(c.totalHours)}</td>
                  <td>{c.lessons}</td>
                  <td>{c.avgMinutes ? `${c.avgMinutes} min` : '-'}</td>
                  <td>{c.busiestDay ? `${fmtDay(c.busiestDay.date, { day: 'numeric', month: 'short' })} (${hoursText(c.busiestDay.hours)}h)` : '-'}</td>
                  <td>{c.zeroDayCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {noCalendar.length > 0 && (
          <p className="th-note">No calendar to read for {noCalendar.map(c => c.name).join(', ')}: they have to connect Google Calendar or link their iPhone calendar in Settings.</p>
        )}
        {iphoneCoaches.length > 0 && (
          <p className="th-note">{iphoneCoaches.map(c => c.name).join(', ')} use the iPhone calendar, which only keeps the last 7 days and the next 60, so their older weeks look empty.</p>
        )}
      </div>

      <div className="th-card">
        <h3 className="th-h">Venues</h3>
        {venues.length === 0 ? <div className="th-empty">No lessons in this period.</div> : (
          <div>
            <Bars rows={venues} valueOf={v => v.hours} labelOf={v => v.name} subOf={v => `${v.share}%`} />
            <p className="th-note">Venue is the first part of the calendar event's location. {venues.some(v => v.name === 'No venue set') ? 'Events with no location are grouped under "No venue set".' : ''}</p>
          </div>
        )}
      </div>

      <div className="th-card">
        <h3 className="th-h">Repeat customers</h3>
        {customers.unique === 0 ? <div className="th-empty">No lessons in this period.</div> : (
          <>
            <div className="th-tiles">
              <div className="th-tile"><b>{customers.unique}</b><span>Different customers</span></div>
              <div className="th-tile"><b>{customers.repeaters}</b><span>Came back (2+)</span></div>
              <div className="th-tile"><b>{customers.repeatRate}%</b><span>Repeat rate</span></div>
              <div className="th-tile"><b>{customers.avgLessonsPerCustomer}</b><span>Lessons per customer</span></div>
            </div>
            <h4 className="th-h" style={{ marginTop: 14, fontSize: 14 }}>How often customers came</h4>
            <Bars rows={customers.frequency} valueOf={f => f.count} labelOf={f => f.label} unitLabel="" />
            <h4 className="th-h" style={{ marginTop: 14, fontSize: 14 }}>Most frequent customers</h4>
            <div className="th-scroll">
              <table className="th-table">
                <thead><tr><th>Customer</th><th>Lessons</th><th>Hours</th><th>First</th><th>Latest</th><th>With</th></tr></thead>
                <tbody>
                  {customers.top.map(c => (
                    <tr key={c.name}>
                      <td>{c.name}</td>
                      <td>{c.lessons}</td>
                      <td>{hoursText(c.hours)}</td>
                      <td>{fmtDay(c.firstDate, { day: 'numeric', month: 'short' })}</td>
                      <td>{fmtDay(c.lastDate, { day: 'numeric', month: 'short' })}</td>
                      <td>{c.coaches.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="th-note">A customer is the name on the calendar event, matched to your students where the name is known.</p>
          </>
        )}
      </div>

      <div className="th-cols">
        <div className="th-card">
          <h3 className="th-h">By day of the week</h3>
          {distribution.byWeekday.map((d, i) => (
            <div key={d.day} className="th-row">
              <span className="th-row-name">{WEEKDAY_FULL[i]}</span>
              <span className="th-bar"><i style={{ width: `${d.hours === 0 ? 0 : Math.max(2, (d.hours / maxWeekday) * 100)}%` }} /></span>
              <span className="th-row-val">{hoursText(d.hours)}h · {d.lessons}</span>
            </div>
          ))}
        </div>

        <div className="th-card">
          <h3 className="th-h">By lesson length</h3>
          <Bars rows={distribution.byDuration} valueOf={d => d.hours} labelOf={d => d.label} subOf={d => `${d.lessons}`} />
          <h3 className="th-h" style={{ marginTop: 14 }}>Share of hours by coach</h3>
          <Bars rows={distribution.byCoach} valueOf={c => c.hours} labelOf={c => c.name} subOf={c => `${c.share}%`} />
        </div>
      </div>

      <div className="th-card">
        <h3 className="th-h">When lessons start</h3>
        {hours.length === 0 ? <div className="th-empty">No lessons in this period.</div> : (
          <>
            <div className="th-mini">
              {hours.map(h => (
                <div key={h.hour} title={`${hourLabel(h.hour)}: ${hoursText(h.hours)}h, ${h.lessons} lessons`}>
                  <i style={{ height: `${Math.max(3, (h.hours / maxHour) * 100)}%` }} />
                </div>
              ))}
            </div>
            <div className="th-mini-labels">{hours.map(h => <span key={h.hour}>{hourLabel(h.hour)}</span>)}</div>
            <p className="th-note">Hours of coaching that start in each hour of the day.</p>
          </>
        )}
      </div>

      <div className="th-card">
        <h3 className="th-h">Days with no lessons</h3>
        <p className="th-sub">Counted up to today.</p>
        <p style={{ margin: '0 0 8px 0' }} className="th-muted"><strong>Whole team ({zeroDays.teamCount})</strong> - nobody taught:</p>
        {zeroDays.team.length === 0 ? <div className="th-muted">Every day had at least one lesson.</div> : (
          <div className="th-chips">{zeroDays.team.map(d => <span key={d} className="th-chip">{fmtDay(d)}</span>)}</div>
        )}
        {zeroDays.teamCount > zeroDays.team.length && <p className="th-note">Showing the latest {zeroDays.team.length}.</p>}
        <h4 className="th-h" style={{ marginTop: 16, fontSize: 14 }}>By coach</h4>
        {coaches.map(c => (
          <div key={c._id} style={{ marginBottom: 8 }}>
            <button
              type="button"
              onClick={() => setOpenCoach(openCoach === c._id ? '' : c._id)}
              style={{ background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer', font: 'inherit', textAlign: 'left', width: '100%' }}
              className="th-row-name"
              aria-expanded={openCoach === c._id}
            >
              {openCoach === c._id ? '▾' : '▸'} {c.name} - <strong>{c.zeroDayCount}</strong> {c.zeroDayCount === 1 ? 'day' : 'days'}
            </button>
            {openCoach === c._id && (
              c.zeroDays.length === 0 ? <div className="th-muted">No empty days.</div> : (
                <div className="th-chips">{c.zeroDays.map(d => <span key={d} className="th-chip">{fmtDay(d)}</span>)}</div>
              )
            )}
          </div>
        ))}
      </div>

      <p className="th-note" style={{ padding: '0 4px 24px' }}>
        Left out of the numbers: {excluded.allDay} all-day {excluded.allDay === 1 ? 'event' : 'events'}, {excluded.buffer} buffer {excluded.buffer === 1 ? 'block' : 'blocks'}, {excluded.tooLong} {excluded.tooLong === 1 ? 'event' : 'events'} longer than 6 hours{excluded.declined ? `, ${excluded.declined} declined` : ''}.
        Updated {new Date(data.generatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}; refreshes every 5 minutes.
      </p>
    </div>
  );
};

export default TeamHoursTab;
